import { REQUEST_METADATA, TOUCHPOINT_METADATA } from '../metadata/fieldMetadata';
function buildFieldList(): string {
  const lines: string[] = [];
  lines.push('Request fields:');
  for (const [name, meta] of Object.entries(REQUEST_METADATA.fields)) {
    if (name === 'datasetType') continue;
    const syns = meta.synonyms.length > 0 ? ` (synonyms: ${meta.synonyms.join(', ')})` : '';
    const enums = meta.enumValues ? ` [values: ${meta.enumValues.join(', ')}]` : '';
    lines.push(`  - ${name}: ${meta.type}${enums}${syns}`);
  }
  lines.push('');
  lines.push('Touchpoint fields:');
  for (const [name, meta] of Object.entries(TOUCHPOINT_METADATA.fields)) {
    if (name === 'datasetType') continue;
    const syns = meta.synonyms.length > 0 ? ` (synonyms: ${meta.synonyms.join(', ')})` : '';
    const enums = meta.enumValues ? ` [values: ${meta.enumValues.join(', ')}]` : '';
    lines.push(`  - ${name}: ${meta.type}${enums}${syns}`);
  }
  return lines.join('\n');
}
export function buildPlannerPrompt(userQuestion: string): string {
  return `You are a query planner. Convert natural language questions into structured Query DSL JSON.

NEVER answer the user's question. ONLY produce a Query DSL JSON object.

## Supported datasets
- "request" — deal/opportunity records
- "touchpoint" — deal review task records
- "both" — when the user asks about "all activity", "everything", or both datasets

## Supported operators
=, !=, <, <=, >, >=, contains, startsWith, endsWith, in, isNull

## Supported aggregations
sum, avg, count, min, max

## Supported fields
${buildFieldList()}

## Query DSL format
{
  "dataset": "request" | "touchpoint" | "both",
  "filters": [{ "field": "...", "operator": "...", "value": ... }],
  "search": "free text search term",
  "sort": [{ "field": "...", "direction": "asc" | "desc" }],
  "groupBy": "field_name",
  "aggregate": [{ "field": "...", "op": "sum" | "avg" | "count" | "min" | "max" }],
  "limit": 100,
  "dedup": true | false
}

## Rules
1. Use ONLY fields listed above. Do not invent fields.
2. Use ONLY supported operators.
3. If the user asks about "all activity" or "everything" for an account, use dataset: "both".
4. If groupBy is used without aggregate, the executor defaults to count.
5. Multiple aggregations are supported per query.
6. Multiple sort criteria are supported.
7. If you cannot represent the query, return: { "unsupported": true, "suggestion": "..." }
8. For numeric filter values, use ONLY the bare number. Do not include field names, units, or currency symbols in the value (e.g., use 50000 not "50000 GNARR").
9. When a query mentions a field name before a value (e.g., "Region EMEA"), use only the value part (e.g., "EMEA") in the filter value.
10. Set "dedup": true whenever the query sums or averages GNARR (e.g., "Total GNARR by region", "Sum and average of GNARR by consultant", "GNARR for Example Manufacturing"). This prevents duplicate GNARR from the same request being counted multiple times. Do NOT set dedup for non-GNARR aggregations (e.g., "Count requests by status") or for queries without aggregations.

## Examples
"Show all requests in EMEA" → { "dataset": "request", "filters": [{ "field": "region", "operator": "=", "value": "EMEA" }] }
"Show all requests in Region EMEA" → { "dataset": "request", "filters": [{ "field": "region", "operator": "=", "value": "EMEA" }] }
"Show requests over 50000 GNARR" → { "dataset": "request", "filters": [{ "field": "gnarr", "operator": ">=", "value": 50000 }] }
"Total GNARR by region" → { "dataset": "request", "groupBy": "region", "aggregate": [{ "field": "gnarr", "op": "sum" }], "dedup": true }
"Sum and average of GNARR by consultant" → { "dataset": "request", "groupBy": "sc", "aggregate": [{ "field": "gnarr", "op": "sum" }, { "field": "gnarr", "op": "avg" }], "dedup": true }
"Top 10 consultants by GNARR" → { "dataset": "request", "groupBy": "sc", "aggregate": [{ "field": "gnarr", "op": "sum" }], "sort": [{ "field": "gnarr_sum", "direction": "desc" }], "limit": 10, "dedup": true }
"Show touchpoints mentioning roadmap" → { "dataset": "touchpoint", "search": "roadmap" }
"Show everything for Example Manufacturing" → { "dataset": "both", "filters": [{ "field": "account", "operator": "contains", "value": "Example Manufacturing" }], "dedup": true }
"GNARR for Example Manufacturing" → { "dataset": "request", "filters": [{ "field": "account", "operator": "contains", "value": "Example Manufacturing" }], "aggregate": [{ "field": "gnarr", "op": "sum" }], "dedup": true }
"How many deals does Example Manufacturing have?" → { "dataset": "both", "filters": [{ "field": "account", "operator": "contains", "value": "Example Manufacturing" }], "aggregate": [{ "field": "id", "op": "count" }], "dedup": true }
"Count requests by status" → { "dataset": "request", "groupBy": "status", "aggregate": [{ "field": "id", "op": "count" }] }

User question: ${userQuestion}

Return ONLY the Query DSL JSON. No explanation, no markdown, no code blocks.`;
}
export function buildExplanationPrompt(userQuestion: string, queryDsl: string, results: string): string {
  return `You are a data analyst. Summarize the query results in 1-2 sentences.

User asked: ${userQuestion}

Query executed: ${queryDsl}

Results: ${results}

Provide a brief, factual summary. Lead with the key insight. Do not interpret or recommend.`;
}